import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listSnapshots,
  createSnapshot,
  deleteSnapshot,
  listPolicies,
  createPolicy,
  patchPolicy,
  deletePolicy
} from "../api/ebsSnapshotApi";

// ─── Snapshot thunks ──────────────────────────────────────────────────────────

export const fetchSnapshots = createAsyncThunk(
  "ebsSnapshots/fetchSnapshots",
  async (desktopRef, { rejectWithValue }) => {
    try {
      const data = await listSnapshots(desktopRef);
      return { desktopRef: desktopRef || null, items: data.items || [] };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const takeSnapshot = createAsyncThunk(
  "ebsSnapshots/takeSnapshot",
  async (payload, { rejectWithValue }) => {
    try {
      return await createSnapshot(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const removeSnapshot = createAsyncThunk(
  "ebsSnapshots/removeSnapshot",
  async (name, { rejectWithValue }) => {
    try {
      await deleteSnapshot(name);
      return name;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Policy thunks ────────────────────────────────────────────────────────────

export const fetchPolicies = createAsyncThunk(
  "ebsSnapshots/fetchPolicies",
  async (desktopRef, { rejectWithValue }) => {
    try {
      const data = await listPolicies(desktopRef);
      return { desktopRef: desktopRef || null, items: data.items || [] };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const addPolicy = createAsyncThunk(
  "ebsSnapshots/addPolicy",
  async (payload, { rejectWithValue }) => {
    try {
      return await createPolicy(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updatePolicy = createAsyncThunk(
  "ebsSnapshots/updatePolicy",
  async ({ name, patch }, { rejectWithValue }) => {
    try {
      return await patchPolicy(name, patch);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const removePolicy = createAsyncThunk(
  "ebsSnapshots/removePolicy",
  async (name, { rejectWithValue }) => {
    try {
      await deletePolicy(name);
      return name;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  // snapshots keyed by desktopRef (or "all")
  snapshots: {},
  policies: {},
  snapshotLoading: false,
  policyLoading: false,
  snapshotError: null,
  policyError: null,
  // per-item loading while deleting
  deletingSnapshots: {},
  deletingPolicies: {}
};

const ebsSnapshotSlice = createSlice({
  name: "ebsSnapshots",
  initialState,
  reducers: {
    clearSnapshotError: (state) => {
      state.snapshotError = null;
    },
    clearPolicyError: (state) => {
      state.policyError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchSnapshots
      .addCase(fetchSnapshots.pending, (state) => {
        state.snapshotLoading = true;
        state.snapshotError = null;
      })
      .addCase(fetchSnapshots.fulfilled, (state, action) => {
        state.snapshotLoading = false;
        const key = action.payload.desktopRef || "all";
        state.snapshots[key] = action.payload.items;
      })
      .addCase(fetchSnapshots.rejected, (state, action) => {
        state.snapshotLoading = false;
        state.snapshotError = action.payload;
      })

      // takeSnapshot
      .addCase(takeSnapshot.fulfilled, (state, action) => {
        const snap = action.payload;
        const key = snap.desktopRef || "all";
        if (!state.snapshots[key]) state.snapshots[key] = [];
        state.snapshots[key].unshift(snap);
      })

      // removeSnapshot
      .addCase(removeSnapshot.pending, (state, action) => {
        state.deletingSnapshots[action.meta.arg] = true;
      })
      .addCase(removeSnapshot.fulfilled, (state, action) => {
        const name = action.payload;
        delete state.deletingSnapshots[name];
        for (const key of Object.keys(state.snapshots)) {
          state.snapshots[key] = state.snapshots[key].filter((s) => s.name !== name);
        }
      })
      .addCase(removeSnapshot.rejected, (state, action) => {
        delete state.deletingSnapshots[action.meta.arg];
      })

      // fetchPolicies
      .addCase(fetchPolicies.pending, (state) => {
        state.policyLoading = true;
        state.policyError = null;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.policyLoading = false;
        const key = action.payload.desktopRef || "all";
        state.policies[key] = action.payload.items;
      })
      .addCase(fetchPolicies.rejected, (state, action) => {
        state.policyLoading = false;
        state.policyError = action.payload;
      })

      // addPolicy
      .addCase(addPolicy.fulfilled, (state, action) => {
        const p = action.payload;
        const key = p.desktopRef || "all";
        if (!state.policies[key]) state.policies[key] = [];
        state.policies[key].unshift(p);
      })

      // updatePolicy
      .addCase(updatePolicy.fulfilled, (state, action) => {
        const updated = action.payload;
        for (const key of Object.keys(state.policies)) {
          const idx = state.policies[key].findIndex((p) => p.name === updated.name);
          if (idx !== -1) state.policies[key][idx] = updated;
        }
      })

      // removePolicy
      .addCase(removePolicy.pending, (state, action) => {
        state.deletingPolicies[action.meta.arg] = true;
      })
      .addCase(removePolicy.fulfilled, (state, action) => {
        const name = action.payload;
        delete state.deletingPolicies[name];
        for (const key of Object.keys(state.policies)) {
          state.policies[key] = state.policies[key].filter((p) => p.name !== name);
        }
      })
      .addCase(removePolicy.rejected, (state, action) => {
        delete state.deletingPolicies[action.meta.arg];
      });
  }
});

export const { clearSnapshotError, clearPolicyError } = ebsSnapshotSlice.actions;
export default ebsSnapshotSlice.reducer;
